import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { aiCategorizationService } from '../../../services/aiCategorizationService';
import { categoryService } from '../../../services/categoryService';
import { accountService } from '../../../services/accountService';

const TransactionFormModal = ({ transaction, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'expense',
    categoryId: '',
    subcategoryId: '',
    accountId: ''
  });

  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  // Check if selected subcategory is 'Transfer Out' or 'Transfer In'
  const isTransferOutSubcategory = availableSubcategories?.find(
    sub => sub?.id === formData?.subcategoryId && sub?.name === 'Transfers Out'
  );
  const isTransferInSubcategory = availableSubcategories?.find(
    sub => sub?.id === formData?.subcategoryId && sub?.name === 'Transfer In'
  );
  const [transferAccountId, setTransferAccountId] = useState('');

  useEffect(() => {
    loadCategories();
    loadAccounts();
  }, []);

  useEffect(() => {
    if (transaction) {
      // First, determine if this transaction uses a subcategory
      let parentCategoryId = '';
      let subcategoryId = '';
      
      if (transaction?.categoryId && categories?.length > 0) {
        const allCats = categories?.flatMap(parent => [parent, ...(parent?.subcategories || [])]);
        const selectedCat = allCats?.find(cat => cat?.id === transaction?.categoryId);
        
        if (selectedCat?.parentId) {
          // This is a subcategory
          parentCategoryId = selectedCat?.parentId;
          subcategoryId = selectedCat?.id;
          const parent = categories?.find(cat => cat?.id === selectedCat?.parentId);
          setSelectedParentCategory(parent);
          setAvailableSubcategories(parent?.subcategories || []);
        } else if (selectedCat) {
          // This is a parent category
          parentCategoryId = selectedCat?.id;
          setSelectedParentCategory(selectedCat);
          setAvailableSubcategories(selectedCat?.subcategories || []);
        }
      }
      
      // Set form data with correct category and subcategory
      setFormData({
        date: transaction?.date?.split('T')?.[0] || '',
        description: transaction?.description || '',
        amount: Math.abs(transaction?.amount)?.toString() || '',
        type: transaction?.type || 'expense',
        categoryId: parentCategoryId || transaction?.categoryId || '',
        subcategoryId: subcategoryId,
        accountId: transaction?.accountId || ''
      });
    }
  }, [transaction, categories]);

  const loadCategories = async () => {
    try {
      const data = await categoryService?.getHierarchical();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService?.getAll();
      setAccounts(data);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  // Trigger AI categorization when description changes
  useEffect(() => {
    const analyzeTransaction = async () => {
      if (formData?.description?.length > 3 && formData?.type && formData?.amount) {
        setIsAnalyzing(true);
        setShowAiSuggestion(false);
        try {
          const suggestion = await aiCategorizationService?.suggestCategory(
            formData?.description,
            formData?.type,
            parseFloat(formData?.amount) || 0
          );
          
          if (suggestion?.categoryId && suggestion?.confidence > 0.5) {
            setAiSuggestion(suggestion);
            setShowAiSuggestion(true);
          } else {
            setAiSuggestion(null);
            setShowAiSuggestion(false);
          }
        } catch (error) {
          console.error('AI categorization error:', error);
          setAiSuggestion(null);
          setShowAiSuggestion(false);
        } finally {
          setIsAnalyzing(false);
        }
      }
    };

    const debounceTimer = setTimeout(analyzeTransaction, 800);
    return () => clearTimeout(debounceTimer);
  }, [formData?.description, formData?.type, formData?.amount]);

  const typeOptions = [
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'payment', label: 'Payment' }
  ];

  // Get parent categories based on transaction type
  const parentCategories = categories?.filter(cat => {
    if (!cat?.parentId) {
      // For payment type, show payment categories
      if (formData?.type === 'payment') {
        return cat?.type === 'payment';
      }
      // For income/expense, show matching type
      return cat?.type === formData?.type;
    }
    return false;
  });

  const categoryOptions = parentCategories?.map(cat => ({
    value: cat?.id,
    label: cat?.emoji ? `${cat?.emoji} ${cat?.name}` : cat?.name
  }));

  // Subcategory options based on selected parent category
  const subcategoryOptions = availableSubcategories?.map(sub => ({
    value: sub?.id,
    label: sub?.name
  }));

  const accountOptions = accounts?.map(acc => ({
    value: acc?.id,
    label: acc?.name
  }));

  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({ ...prev, categoryId, subcategoryId: '' }));
    
    // Find the selected parent category and set available subcategories
    const parent = categories?.find(cat => cat?.id === categoryId);
    setSelectedParentCategory(parent);
    setAvailableSubcategories(parent?.subcategories || []);
    
    // Clear AI suggestion when user manually changes category
    setShowAiSuggestion(false);
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (subcategoryId) => {
    setFormData(prev => ({ ...prev, subcategoryId }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const amount = parseFloat(formData?.amount);
    
    // Determine amount sign based on transaction type and account
    let finalAmount;
    const selectedAccount = accounts?.find(acc => acc?.id === formData?.accountId);
    const isLiabilityAccount = selectedAccount?.group === 'Liabilities';
    
    if (isLiabilityAccount) {
      // For liability accounts:
      // - Expenses (charges) INCREASE debt → positive
      // - Income/Payment DECREASE debt → negative
      if (formData?.type === 'expense') {
        finalAmount = Math.abs(amount);
      } else {
        finalAmount = -Math.abs(amount);
      }
    } else {
      // For asset/cash accounts:
      // - Income INCREASES balance → positive
      // - Expense/Payment DECREASE balance → negative
      if (formData?.type === 'income') {
        finalAmount = Math.abs(amount);
      } else {
        finalAmount = -Math.abs(amount);
      }
    }

    onSubmit({
      date: new Date(formData?.date)?.toISOString(),
      description: formData?.description,
      amount: finalAmount,
      type: formData?.type,
      categoryId: formData?.subcategoryId || formData?.categoryId,
      accountId: formData?.accountId
    });

    // Only create matching transactions for NEW transactions (not edits)
    if (!transaction) {
      // Handle matching transaction for Transfer Out subcategory
      if (isTransferOutSubcategory && transferAccountId) {
        handleTransferOutMatching(amount);
      }

      // Handle matching transaction for Transfer In subcategory
      if (isTransferInSubcategory && transferAccountId) {
        handleTransferInMatching(amount);
      }
    }
  };

  const handleTransferOutMatching = async (amount) => {
    try {
      const { transactionService } = await import('../../../services/transactionService');
      const { categoryService } = await import('../../../services/categoryService');
      
      // Find Transfer In subcategory for the matching transaction
      const allCategories = await categoryService?.getHierarchical();
      const transfersOtherCategory = allCategories?.find(
        cat => cat?.name === 'Transfers & Other' && cat?.type === 'income'
      );
      const transferInSubcategory = transfersOtherCategory?.subcategories?.find(
        sub => sub?.name === 'Transfer In'
      );

      if (transferInSubcategory) {
        await transactionService?.create({
          accountId: transferAccountId,
          categoryId: transferInSubcategory?.id,
          date: new Date(formData?.date)?.toISOString(),
          description: `Transfer from account`,
          amount: Math.abs(amount),
          type: 'income',
          cleared: false
        });
      }
    } catch (error) {
      console.error('Error creating matching Transfer In transaction:', error);
    }
  };

  const handleTransferInMatching = async (amount) => {
    try {
      const { transactionService } = await import('../../../services/transactionService');
      const { categoryService } = await import('../../../services/categoryService');
      
      // Find Transfers Out subcategory for the matching transaction
      const allCategories = await categoryService?.getHierarchical();
      const transfersAdjustmentsCategory = allCategories?.find(
        cat => cat?.name === 'Transfers & Adjustments' && cat?.type === 'expense'
      );
      const transferOutSubcategory = transfersAdjustmentsCategory?.subcategories?.find(
        sub => sub?.name === 'Transfers Out'
      );

      if (transferOutSubcategory) {
        await transactionService?.create({
          accountId: transferAccountId,
          categoryId: transferOutSubcategory?.id,
          date: new Date(formData?.date)?.toISOString(),
          description: `Transfer to account`,
          amount: -Math.abs(amount),
          type: 'expense',
          cleared: false
        });
      }
    } catch (error) {
      console.error('Error creating matching Transfer Out transaction:', error);
    }
  };

  const handleChange = (field, value) => {
    // When type changes, reset category and subcategory selections
    if (field === 'type') {
      setFormData(prev => ({ ...prev, [field]: value, categoryId: '', subcategoryId: '' }));
      setSelectedParentCategory(null);
      setAvailableSubcategories([]);
      setShowAiSuggestion(false);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear AI suggestion if user manually changes category or subcategory
    if (field === 'categoryId' || field === 'subcategoryId') {
      setShowAiSuggestion(false);
    }
  };

  const handleAcceptSuggestion = () => {
    if (aiSuggestion?.categoryId) {
      setFormData(prev => ({ ...prev, categoryId: aiSuggestion?.categoryId }));
      setShowAiSuggestion(false);
    }
  };

  const handleDismissSuggestion = () => {
    setShowAiSuggestion(false);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-[600px] mx-16 max-h-[90vh] overflow-y-auto">
        <div className="p-20 md:p-24 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-12">
            <Icon name="FileText" size={24} color="var(--color-primary)" />
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              {transaction ? 'Edit Transaction' : 'Add Transaction'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-20 md:p-24">
          <div className="space-y-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData?.date}
                  onChange={(e) => handleChange('date', e?.target?.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Type *
                </label>
                <Select
                  options={typeOptions}
                  value={formData?.type}
                  onChange={(value) => handleChange('type', value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Amount *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.amount}
                  onChange={(e) => handleChange('amount', e?.target?.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Account *
                </label>
                <Select
                  options={accountOptions}
                  value={formData?.accountId}
                  onChange={(value) => handleChange('accountId', value)}
                  placeholder="Select account"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Description *
              </label>
              <Input
                type="text"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="e.g., Grocery shopping at Whole Foods"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-caption font-medium text-foreground mb-8">
                Category *
              </label>
              <Select
                options={categoryOptions}
                value={formData?.categoryId}
                onChange={handleCategoryChange}
                placeholder="Select a category"
                required
              />
            </div>

            {/* Subcategory dropdown - only show if parent category is selected and has subcategories */}
            {formData?.categoryId && availableSubcategories?.length > 0 && (
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Subcategory (Optional)
                </label>
                <Select
                  options={subcategoryOptions}
                  value={formData?.subcategoryId}
                  onChange={handleSubcategoryChange}
                  placeholder="Select a subcategory (optional)"
                />
              </div>
            )}

            {/* Show Transfer Account selector for Transfer Out subcategory */}
            {isTransferOutSubcategory && (
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Transfer To Account (Optional)
                </label>
                <Select
                  options={accountOptions}
                  value={transferAccountId}
                  onChange={(value) => setTransferAccountId(value)}
                  placeholder="Select destination account"
                />
                <p className="text-xs text-muted-foreground mt-4">
                  Optional: Select an account to automatically create a corresponding Transfer In transaction
                </p>
              </div>
            )}

            {/* Show Transfer Account selector for Transfer In subcategory */}
            {isTransferInSubcategory && (
              <div>
                <label className="block text-sm font-caption font-medium text-foreground mb-8">
                  Transfer From Account (Optional)
                </label>
                <Select
                  options={accountOptions}
                  value={transferAccountId}
                  onChange={(value) => setTransferAccountId(value)}
                  placeholder="Select source account"
                />
                <p className="text-xs text-muted-foreground mt-4">
                  Optional: Select an account to automatically create a corresponding Transfer Out transaction
                </p>
              </div>
            )}

            {showAiSuggestion && aiSuggestion && (
              <div className="mb-12 p-12 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex items-start justify-between gap-12">
                  <div className="flex-1">
                    <div className="flex items-center gap-8 mb-4">
                      <Icon name="Sparkles" size={16} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-primary">AI Suggestion</span>
                      <span className="text-xs px-6 py-2 bg-primary/20 rounded-full text-primary">
                        {Math.round(aiSuggestion?.confidence * 100)}% confident
                      </span>
                    </div>
                    <p className="text-sm text-foreground font-medium mb-4">
                      {aiSuggestion?.categoryName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {aiSuggestion?.reasoning}
                    </p>
                  </div>
                  <div className="flex gap-8">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAcceptSuggestion}
                      className="text-primary hover:text-primary"
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleDismissSuggestion}
                    >
                      <Icon name="X" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-12 mt-24">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
            >
              {transaction ? 'Update' : 'Add'} Transaction
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionFormModal;